import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from "react";
import { api, setUnauthorizedHandler } from "../api";
import { outcomeAfterSessionCheck, rememberSignedIn, wasSignedIn } from "./offline";

type AuthState = "loading" | "anonymous" | "authenticated";

interface AuthValue {
  state: AuthState;
  login: (password: string) => Promise<void>;
  logout: () => Promise<void>;
}

const AuthContext = createContext<AuthValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<AuthState>("loading");

  // The session lives in an HttpOnly cookie that JavaScript cannot read, so asking the
  // server is the only way to know whether we are logged in.
  useEffect(() => {
    let cancelled = false;
    api
      .session()
      .then(() => {
        if (cancelled) return;
        rememberSignedIn(true);
        setState("authenticated");
      })
      .catch((error: unknown) => {
        if (cancelled) return;
        // No signal is not "logged out": an installed app opened offline stays signed in
        // and shows its own offline notice. Only a real 401 clears the flag.
        const outcome = outcomeAfterSessionCheck(error, wasSignedIn());
        if (outcome === "anonymous") rememberSignedIn(false);
        setState(outcome === "anonymous" ? "anonymous" : "authenticated");
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    setUnauthorizedHandler(() => {
      rememberSignedIn(false);
      setState("anonymous");
    });
    return () => setUnauthorizedHandler(null);
  }, []);

  const login = useCallback(async (password: string) => {
    await api.login(password);
    rememberSignedIn(true);
    setState("authenticated");
  }, []);

  const logout = useCallback(async () => {
    await api.logout().catch(() => undefined);
    rememberSignedIn(false);
    setState("anonymous");
  }, []);

  const value = useMemo(() => ({ state, login, logout }), [state, login, logout]);
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthValue {
  const value = useContext(AuthContext);
  if (!value) throw new Error("useAuth must be used inside AuthProvider");
  return value;
}
